import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import {
  ArrowRight, Code2, Smartphone, Globe, Cpu, Database, ChevronRight,
  MapPin, Mail, Check, Star, Zap, Shield,
  Facebook, Linkedin, Instagram, MessageCircle,
  Building2, Trophy, Brain, TrendingUp, Clock, Award, CheckCircle,
  Users, GraduationCap, Utensils, Truck, Layers, Package,
} from 'lucide-react';
import { Navigation, Footer } from '../Components/Common/Navfot';

/* ═══════════════════════  DATA  ═══════════════════════ */

const STATS = [
  { end: 57, suffix: '+', label: 'Projects Delivered', sub: 'India & International' },
  { end: 4,  suffix: '',  label: 'Countries Served',   sub: 'India · UK · Canada · Cambodia' },
  { end: 50, suffix: '+', label: 'India Projects',     sub: 'Across 10+ industries' },
  { end: 7,  suffix: '+', label: 'International',      sub: 'UK · Canada · Cambodia' },
];

const INDIA_PROJECTS = [
  {
    icon: Building2,
    type: 'Geotechnical & Infrastructure Company',
    location: 'Lucknow, Uttar Pradesh',
    service: 'Employee Management System (EMS)',
    features: ['Payroll & Accounting', 'Attendance & Leave', 'Employee Onboarding', 'Task Assignment', 'Stock Management', 'Mobile App + Web Portal'],
    color: 'border-blue-200 bg-blue-50/40',
    tag: 'bg-blue-100 text-blue-700',
  },
  {
    icon: GraduationCap,
    type: 'Leading State University — Complete University Portal',
    location: 'Uttar Pradesh, India',
    service: 'University Management System',
    featured: true,
    features: [
      'Online Admission & Enrollment',
      'Semester & Bus Fee Collection',
      'Bus Route Allotment',
      'Hostel Allotment System',
      'Student Course & Class Allotment',
      'Semester Promotion & Result',
      'Faculty Job Applications & Selection',
      'Employee Salary Slips',
      'Research Journal & Publication Upload',
    ],
    color: 'border-indigo-200 bg-indigo-50/40',
    tag: 'bg-indigo-100 text-indigo-700',
  },
  {
    icon: Layers,
    type: 'Building Materials & Hardware Retailer',
    location: 'Gorakhpur, Uttar Pradesh',
    service: 'Billing & Stock Management Software',
    features: ['Product Inventory', 'Billing & Invoicing', 'Supplier Tracking', 'Sales Reports'],
    color: 'border-slate-200 bg-slate-50/40',
    tag: 'bg-slate-100 text-slate-700',
  },
];

const INTL_PROJECTS = [
  {
    icon: Utensils,
    type: 'Fine Dining Restaurant',
    location: 'Portsmouth, United Kingdom 🇬🇧',
    service: 'Online Booking & Ordering Platform',
    features: ['Table Reservation System', 'Online Food Ordering', 'Menu Management', 'Restaurant Dashboard'],
    color: 'border-amber-200 bg-amber-50/40',
    tag: 'bg-amber-100 text-amber-700',
  },
  {
    icon: Truck,
    type: 'Moving & Logistics Company',
    location: 'Canada 🇨🇦',
    service: 'Operations Management Software',
    features: ['Job Scheduling', 'Customer Management', 'Invoicing & Billing', 'Route Coordination'],
    color: 'border-red-200 bg-red-50/40',
    tag: 'bg-red-100 text-red-700',
  },
  {
    icon: Package,
    type: 'Indian Grocery & Retail Stores (×2)',
    location: 'Sihanoukville & Phnom Penh, Cambodia 🇰🇭',
    service: 'Inventory & Import-Export Management',
    features: ['Multi-Branch Inventory', 'Wholesale & Retail Ops', 'Import Operations', 'POS Integration'],
    color: 'border-emerald-200 bg-emerald-50/40',
    tag: 'bg-emerald-100 text-emerald-700',
  },
];

const TECH_GROUPS = [
  { label: 'Frontend',       items: ['React.js', 'Next.js', 'TypeScript', 'Tailwind CSS'] },
  { label: 'Backend',        items: ['Node.js', 'Python', 'Django', 'PostgreSQL'] },
  { label: 'Mobile',         items: ['Flutter', 'React Native', 'iOS', 'Android'] },
  { label: 'Cloud & DevOps', items: ['AWS', 'Docker', 'CI/CD', 'Nginx'] },
];

const PROCESS = [
  { n: '01', title: 'Discovery',        desc: 'Deep-dive into your business goals, technical requirements and success metrics.' },
  { n: '02', title: 'Architecture',     desc: 'System design, UI/UX wireframes, database schema and full technical blueprint.' },
  { n: '03', title: 'Development',      desc: 'Agile sprints with continuous QA — websites in 4–6 weeks, apps in 3–6 months.' },
  { n: '04', title: 'Launch & Support', desc: 'Post-launch monitoring, iterative improvements and 24/7 dedicated support.' },
];

const INDUSTRIES = [
  'E-Commerce', 'Healthcare', 'Education & University', 'Manufacturing',
  'Restaurant & Food', 'Pharmacy', 'Retail & Hardware', 'Real Estate',
  'Distribution & Logistics', 'Infrastructure', 'Moving & Transport', 'Finance',
];

const HERO_SLIDES = [
  {
    id: 'india',
    flag: '🇮🇳',
    country: 'India',
    region: 'South Asia · Primary Market',
    accent: '#3B82F6',
    glow: 'rgba(59,130,246,0.32)',
    border: 'border-blue-500/40',
    count: '50+',
    unit: 'Projects',
    items: [
      { icon: '🏛️', label: 'University Management Portal' },
      { icon: '🏭', label: 'ERP & Employee Management' },
      { icon: '🛒', label: 'Retail & POS Solutions' },
      { icon: '📱', label: 'Mobile Apps & Web Portals' },
    ],
    tag: 'India · 10+ Industries',
    tagColor: 'text-blue-300 border-blue-500/30 bg-blue-500/10',
  },
  {
    id: 'uk',
    flag: '🇬🇧',
    country: 'United Kingdom',
    region: 'Europe · Portsmouth',
    accent: '#A855F7',
    glow: 'rgba(168,85,247,0.32)',
    border: 'border-purple-500/40',
    count: '2',
    unit: 'Projects',
    items: [
      { icon: '🍽️', label: 'Fine Dining Restaurant Platform' },
      { icon: '📋', label: 'Table Reservation System' },
      { icon: '🛍️', label: 'Online Food Ordering' },
      { icon: '📊', label: 'Restaurant Analytics Dashboard' },
    ],
    tag: 'Portsmouth, UK',
    tagColor: 'text-purple-300 border-purple-500/30 bg-purple-500/10',
  },
  {
    id: 'canada',
    flag: '🇨🇦',
    country: 'Canada',
    region: 'North America',
    accent: '#EF4444',
    glow: 'rgba(239,68,68,0.32)',
    border: 'border-red-500/40',
    count: '1',
    unit: 'Project',
    items: [
      { icon: '🚚', label: 'Moving & Logistics Platform' },
      { icon: '📦', label: 'Job Booking & Scheduling' },
      { icon: '🗺️', label: 'Route & Fleet Management' },
      { icon: '💳', label: 'Invoicing & Online Payments' },
    ],
    tag: 'Canada-wide Operations',
    tagColor: 'text-red-300 border-red-500/30 bg-red-500/10',
  },
  {
    id: 'cambodia',
    flag: '🇰🇭',
    country: 'Cambodia',
    region: 'South-East Asia',
    accent: '#10B981',
    glow: 'rgba(16,185,129,0.32)',
    border: 'border-emerald-500/40',
    count: '2',
    unit: 'Projects',
    items: [
      { icon: '🛍️', label: 'Indian Grocery Retail Stores' },
      { icon: '💰', label: 'Billing & Inventory System' },
      { icon: '📦', label: 'Wholesale & Import Operations' },
      { icon: '🌏', label: 'Phnom Penh & Sihanoukville' },
    ],
    tag: 'SE Asia · 2 Cities',
    tagColor: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10',
  },
];


/* ═══════════════════════  ANIMATION  ═══════════════════════ */

const fade    = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } } };
const stagger = { show: { transition: { staggerChildren: 0.09 } } };

function Section({ id, className = '', children }) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section id={id} ref={ref} className={className}
      variants={stagger} initial="hidden" animate={inView ? 'show' : 'hidden'}>
      {children}
    </motion.section>
  );
}

function SectionLabel({ children, light }) {
  return (
    <motion.span variants={fade}
      className={`inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-3 ${
        light
          ? 'text-blue-300 bg-blue-500/15 border border-blue-500/20'
          : 'text-blue-600 bg-blue-50'
      }`}>
      {children}
    </motion.span>
  );
}

/* ═══════════════════════  COUNTER HOOK  ═══════════════════════ */

function useCounter(target, active) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let n = 0;
    const step = target / 60;
    const id = setInterval(() => {
      n += step;
      if (n >= target) { setVal(target); clearInterval(id); }
      else setVal(Math.floor(n));
    }, 20);
    return () => clearInterval(id);
  }, [active, target]);
  return val;
}

/* ═══════════════════════  HERO  ═══════════════════════ */

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-screen bg-[#060614] flex flex-col justify-center overflow-hidden pt-16">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-blue-700/20 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-indigo-600/20 blur-[100px]" />
      </div>
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-5 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-blue-400 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-8">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              CIN Registered · Noida, India · Est. October 2025
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl sm:text-6xl md:text-7xl font-black text-white leading-[0.93] tracking-tight mb-6">
              Enterprise
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-400 bg-clip-text text-transparent">
                Software
              </span>
              <br />
              Built Right.
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              className="text-gray-400 text-lg md:text-xl max-w-xl leading-relaxed mb-10">
              Custom ERP, CRM, University Portals, POS & mobile solutions trusted by businesses
              across <strong className="text-white font-semibold">India, UK, Canada & Cambodia</strong>.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
              className="flex flex-col sm:flex-row gap-3 mb-10">
              <button onClick={() => navigate('/contact')}
                className="group flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-4 rounded-2xl text-base transition-all shadow-lg shadow-blue-600/30 hover:-translate-y-0.5">
                Start a Project <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => navigate('/services')}
                className="flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 text-white/80 hover:text-white font-semibold px-8 py-4 rounded-2xl text-base transition-all">
                Explore Services <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
              className="flex flex-wrap gap-2">
              {[
                { flag: '🇮🇳', label: 'India',    count: '50+ projects' },
                { flag: '🇬🇧', label: 'UK',       count: '2 projects' },
                { flag: '🇨🇦', label: 'Canada',   count: '1 project' },
                { flag: '🇰🇭', label: 'Cambodia', count: '2 projects' },
              ].map(({ flag, label, count }) => (
                <span key={label} className="flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/70 text-xs font-medium px-3 py-1.5 rounded-full">
                  <span className="text-sm">{flag}</span>
                  <span className="text-white/90 font-semibold">{label}</span>
                  <span className="text-white/40">·</span>
                  {count}
                </span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: 'easeOut' }}
            className="hidden lg:flex justify-center items-center py-12">
            <HeroCard />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function HeroCard() {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);

  useEffect(() => {
    const id = setInterval(() => {
      setDir(1);
      setIdx(i => (i + 1) % HERO_SLIDES.length);
    }, 5500);
    return () => clearInterval(id);
  }, []);

  const slide = HERO_SLIDES[idx];

  const go = (next) => {
    if (next === idx) return;
    setDir(next > idx ? 1 : -1);
    setIdx(next);
  };

  const variants = {
    enter:  (d) => ({ rotateY: d > 0 ? 90 : -90, opacity: 0, scale: 0.88 }),
    center: {       rotateY: 0,  opacity: 1, scale: 1 },
    exit:   (d) => ({ rotateY: d > 0 ? -70 : 70, opacity: 0, scale: 0.92 }),
  };

  return (
    <div className="relative w-full max-w-[420px] select-none pb-2">

      {/* Animated colour glow — crossfades per slide */}
      <AnimatePresence>
        <motion.div
          key={`glow-${slide.id}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute pointer-events-none"
          style={{
            inset: '-60px',
            background: `radial-gradient(ellipse 65% 55% at 50% 58%, ${slide.glow}, transparent 70%)`,
            filter: 'blur(24px)',
          }}
        />
      </AnimatePresence>

      {/* 3-D perspective stage */}
      <div style={{ perspective: '1200px' }}>
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={slide.id}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformStyle: 'preserve-3d', boxShadow: `0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px ${slide.accent}22` }}
            className={`relative bg-[#080d18]/96 backdrop-blur-md border ${slide.border} rounded-2xl overflow-hidden`}
          >
            {/* Auto-advance progress bar */}
            <div className="h-[2px] bg-white/5 overflow-hidden">
              <motion.div
                key={`prog-${slide.id}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 5.5, ease: 'linear' }}
                style={{ height: '100%', background: slide.accent, transformOrigin: 'left' }}
              />
            </div>

            {/* macOS-style title bar */}
            <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10 bg-white/[0.02]">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
              <span className="ml-3 text-[10px] text-gray-500 font-mono">vibhraad.in · global-projects</span>
              <span className="ml-auto text-[10px] font-mono font-bold" style={{ color: slide.accent }}>
                {idx + 1} / {HERO_SLIDES.length}
              </span>
            </div>

            <div className="p-5 min-h-[340px] flex flex-col">
              {/* Country header row */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center border border-white/10 flex-shrink-0"
                    style={{ background: `${slide.accent}18` }}
                  >
                    <span className="text-3xl leading-none">{slide.flag}</span>
                  </div>
                  <div>
                    <p className="text-white font-black text-base leading-snug">{slide.country}</p>
                    <p className="text-gray-500 text-[11px] mt-0.5">{slide.region}</p>
                  </div>
                </div>
                <div
                  className="text-right px-3 py-2 rounded-xl border border-white/10"
                  style={{ background: `${slide.accent}12` }}
                >
                  <p className="font-black text-2xl leading-none" style={{ color: slide.accent }}>{slide.count}</p>
                  <p className="text-gray-500 text-[10px] mt-0.5">{slide.unit}</p>
                </div>
              </div>

              {/* Accent divider */}
              <div
                className="h-px mb-4"
                style={{ background: `linear-gradient(to right, ${slide.accent}55, transparent)` }}
              />

              {/* Feature items — stagger in after card lands */}
              <div className="space-y-2 mb-4">
                {slide.items.map((item, i) => (
                  <motion.div
                    key={`${slide.id}-${i}`}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.075, ease: 'easeOut' }}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 border border-white/[0.07]"
                    style={{ background: `${slide.accent}0a` }}
                  >
                    <span className="text-sm flex-shrink-0">{item.icon}</span>
                    <span className="text-gray-300 text-xs font-medium flex-1">{item.label}</span>
                    <div
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: slide.accent, opacity: 0.55 }}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Tag badge */}
              <div className={`mt-auto inline-flex items-center gap-2 border text-[10px] font-semibold px-3 py-1.5 rounded-full ${slide.tagColor}`}>
                <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: slide.accent }} />
                {slide.tag}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation dots */}
      <div className="flex justify-center items-center gap-2.5 mt-5">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => go(i)}
            className="rounded-full transition-all duration-300 focus:outline-none"
            style={{
              width:      i === idx ? 28 : 8,
              height:     8,
              background: i === idx ? slide.accent : 'rgba(255,255,255,0.2)',
            }}
          />
        ))}
      </div>

      {/* Floating "57+ Projects Live" badge */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="absolute -bottom-4 -right-4 bg-white rounded-2xl px-4 py-3 shadow-xl border border-gray-100 z-20"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-800">57+ Projects Live</div>
            <div className="text-[10px] text-gray-400">Across 4 countries</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ═══════════════════════  STATS + TRUST  ═══════════════════════ */

function StatsAndTrust() {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true });

  function Counter({ end, suffix }) {
    const val = useCounter(end, inView);
    return <span>{val}{suffix}</span>;
  }

  return (
    <div ref={ref}>
      <div className="bg-gray-950">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-white/10">
            {STATS.map(({ end, suffix, label, sub }) => (
              <div key={label} className="text-center px-6 py-8">
                <div className="text-4xl md:text-5xl font-black text-white mb-1 tabular-nums">
                  <Counter end={end} suffix={suffix} />
                </div>
                <div className="text-gray-200 font-semibold text-sm mb-0.5">{label}</div>
                <div className="text-gray-500 text-xs">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex flex-wrap justify-center md:justify-between gap-4">
            {[
              { icon: Award,      label: 'CIN Registered Company' },
              { icon: Shield,     label: 'ISO-Aligned Processes' },
              { icon: TrendingUp, label: '50+ India Projects' },
              { icon: Globe,      label: '4 Countries Served' },
              { icon: Clock,      label: '24/7 Post-Launch Support' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors">
                <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <span className="text-sm font-medium whitespace-nowrap">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════  PROJECTS + SERVICES  ═══════════════════════ */

function ProjectsSection() {
  return (
    <Section id="projects" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-5">

        <div className="text-center mb-10">
          <SectionLabel>What We Build · Our Work</SectionLabel>
          <motion.h2 variants={fade} className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            57+ Projects · 4 Countries
          </motion.h2>
          <motion.p variants={fade} className="text-gray-500 max-w-2xl mx-auto">
            Client data is confidential — we show the business type and service delivered, not names.
          </motion.p>
        </div>

        {/* Service capability tags */}
        <motion.div variants={fade} className="flex flex-wrap justify-center gap-2 mb-12">
          {[
            { icon: Code2,         label: 'Custom Software' },
            { icon: GraduationCap, label: 'University Portals' },
            { icon: Smartphone,    label: 'Web & Mobile Apps' },
            { icon: Cpu,           label: 'POS & Billing' },
            { icon: Database,      label: 'ERP & CRM' },
            { icon: Globe,         label: 'Digital Marketing' },
            { icon: Brain,         label: 'AI & Cloud' },
          ].map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm hover:border-blue-300 hover:text-blue-600 transition-colors cursor-default">
              <Icon className="w-3.5 h-3.5" /> {label}
            </span>
          ))}
        </motion.div>

        {/* Country count strip */}
        <motion.div variants={fade} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {[
            { flag: '🇮🇳', country: 'India',    count: '50+', color: 'bg-blue-600' },
            { flag: '🇬🇧', country: 'UK',        count: '2',   color: 'bg-indigo-600' },
            { flag: '🇨🇦', country: 'Canada',   count: '1',   color: 'bg-red-600' },
            { flag: '🇰🇭', country: 'Cambodia', count: '2',   color: 'bg-emerald-600' },
          ].map(({ flag, country, count, color }) => (
            <div key={country} className={`${color} rounded-2xl p-5 text-center text-white`}>
              <div className="text-3xl mb-1">{flag}</div>
              <div className="text-3xl font-black">{count}</div>
              <div className="text-white/70 text-sm font-medium">projects · {country}</div>
            </div>
          ))}
        </motion.div>

        {/* India */}
        <div className="mb-12">
          <motion.div variants={fade} className="flex items-center gap-3 mb-6">
            <span className="text-2xl">🇮🇳</span>
            <h3 className="text-xl font-black text-gray-900">India Projects (50+)</h3>
            <div className="flex-1 h-px bg-gray-200" />
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {INDIA_PROJECTS.map((proj) => {
              const Icon = proj.icon;
              return (
                <motion.div key={proj.type} variants={fade}
                  className={`rounded-2xl border-2 p-6 ${proj.color} ${proj.featured ? 'md:col-span-3' : ''} hover:shadow-md transition-shadow`}>
                  {proj.featured && (
                    <span className="inline-block bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
                      Featured Project
                    </span>
                  )}
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className={`inline-flex text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-2 ${proj.tag}`}>
                        {proj.type}
                      </div>
                      <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {proj.location}
                      </p>
                      <p className="text-sm font-bold text-gray-800 mb-3">{proj.service}</p>
                      <div className={`grid gap-1.5 ${proj.featured ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1'}`}>
                        {proj.features.map(f => (
                          <div key={f} className="flex items-center gap-2 text-xs text-gray-600">
                            <Check className="w-3 h-3 text-blue-500 flex-shrink-0" /> {f}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            <motion.div variants={fade} className="rounded-2xl border-2 border-gray-200 bg-gray-50 p-6">
              <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <span className="text-white font-black text-lg">47+</span>
              </div>
              <p className="font-bold text-gray-800 mb-2">More India Projects</p>
              <p className="text-xs text-gray-500 leading-relaxed mb-3">
                E-commerce, healthcare portals, real estate CRM, restaurant POS, pharmacy management, fashion retail, school management & more.
              </p>
              <Link to="/contact" className="text-xs font-semibold text-blue-600 hover:text-blue-500 inline-flex items-center gap-1 transition-colors">
                Discuss Your Project <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* International */}
        <div>
          <motion.div variants={fade} className="flex items-center gap-3 mb-6">
            <span className="text-2xl">🌍</span>
            <h3 className="text-xl font-black text-gray-900">International Projects (7+)</h3>
            <div className="flex-1 h-px bg-gray-200" />
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {INTL_PROJECTS.map((proj) => {
              const Icon = proj.icon;
              return (
                <motion.div key={proj.type} variants={fade}
                  className={`rounded-2xl border-2 p-6 ${proj.color} hover:shadow-md transition-shadow`}>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className={`inline-flex text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-2 ${proj.tag}`}>
                        {proj.type}
                      </div>
                      <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {proj.location}
                      </p>
                      <p className="text-sm font-bold text-gray-800 mb-3">{proj.service}</p>
                      <div className="space-y-1.5">
                        {proj.features.map(f => (
                          <div key={f} className="flex items-center gap-2 text-xs text-gray-600">
                            <Check className="w-3 h-3 text-blue-500 flex-shrink-0" /> {f}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════  ERP + PROCESS  ═══════════════════════ */

function SolutionsAndProcess() {
  return (
    <section className="py-20 bg-gray-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: 'radial-gradient(circle, #6b7280 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
      <div className="relative max-w-7xl mx-auto px-5">
        <div className="text-center mb-12">
          <SectionLabel light>Complete Business Systems</SectionLabel>
          <h2 className="text-3xl md:text-4xl font-black text-white mt-2 mb-3">
            ERP · CRM · POS · University · Workforce
          </h2>
          <p className="text-gray-400 text-sm max-w-2xl mx-auto">
            Industry-specific systems — from billing to payroll, from student results to pipeline management.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-20">
          {[
            { label: 'University & Education', color: 'border-indigo-500/30 bg-indigo-950/20', dot: 'bg-indigo-400', items: ['Admission Portal', 'Fee Collection', 'Bus & Hostel Allotment', 'Course & Semester Mgmt', 'Result & Promotion', 'Faculty & HR Module'] },
            { label: 'ERP & POS',              color: 'border-blue-500/30 bg-blue-950/20',     dot: 'bg-blue-500',   items: ['Manufacturing ERP', 'Inventory & Stock', 'Restaurant & Retail POS', 'Pharmacy POS', 'Hardware Store Billing'] },
            { label: 'CRM & HR',               color: 'border-violet-500/30 bg-violet-950/20', dot: 'bg-violet-500', items: ['Sales & Service CRM', 'Real Estate CRM', 'Attendance System', 'Payroll & Salary Slip', 'Task Management'] },
            { label: 'Workforce',              color: 'border-amber-500/30 bg-amber-950/20',   dot: 'bg-amber-500',  items: ['Employee Onboarding', 'Leave Management', 'Performance Review', 'Research & Publications', 'Location Tracking'] },
          ].map(({ label, color, dot, items }) => (
            <div key={label} className={`rounded-2xl border p-5 ${color} backdrop-blur-sm`}>
              <div className="flex items-center gap-2 mb-4">
                <span className={`w-2 h-2 rounded-full ${dot}`} />
                <span className="text-white font-black text-sm">{label}</span>
              </div>
              <ul className="space-y-2">
                {items.map(item => (
                  <li key={item} className="flex items-center gap-2 text-gray-400 text-xs">
                    <span className={`w-1 h-1 rounded-full flex-shrink-0 ${dot}`} /> {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-16">
          <div className="text-center mb-10">
            <SectionLabel light>How We Work</SectionLabel>
            <h2 className="text-3xl font-black text-white mt-2">Our 4-Stage Delivery Process</h2>
          </div>
          <div className="relative grid md:grid-cols-4 gap-8">
            <div className="hidden md:block absolute top-7 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-blue-600/40 to-transparent pointer-events-none" />
            {PROCESS.map(({ n, title, desc }) => (
              <div key={n} className="relative text-center">
                <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-600/20 relative z-10">
                  <span className="text-white font-black">{n}</span>
                </div>
                <h3 className="text-sm font-bold text-white mb-2">{title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════  TECH + INDUSTRIES  ═══════════════════════ */

function TechAndIndustries() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-5">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <SectionLabel>Technology Stack</SectionLabel>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">Modern Stack. Proven Results.</h2>
            <div className="grid grid-cols-2 gap-4">
              {TECH_GROUPS.map(({ label, items }) => (
                <div key={label} className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-3">{label}</div>
                  <div className="space-y-2.5">
                    {items.map(t => (
                      <div key={t} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                        <span className="text-gray-700 text-sm font-medium">{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <SectionLabel>Industry Expertise</SectionLabel>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">Industries We Serve</h2>
            <div className="flex flex-wrap gap-2.5 mb-8">
              {INDUSTRIES.map(ind => (
                <span key={ind}
                  className="px-4 py-2 bg-blue-50 border border-blue-100 text-blue-700 rounded-xl text-sm font-medium hover:bg-blue-100 transition-all cursor-default">
                  {ind}
                </span>
              ))}
            </div>
            <div className="bg-blue-600 rounded-2xl p-6 text-white">
              <div className="text-2xl font-black mb-1">₹50K+</div>
              <div className="text-blue-100 text-sm mb-4">Starting budget for custom software projects</div>
              <div className="flex gap-3">
                <Link to="/pricing" className="text-xs font-semibold bg-white text-blue-600 px-4 py-2 rounded-xl hover:bg-blue-50 transition-colors">
                  View Pricing
                </Link>
                <Link to="/contact" className="text-xs font-semibold border border-white/30 text-white px-4 py-2 rounded-xl hover:bg-white/10 transition-colors">
                  Get a Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════  CTA  ═══════════════════════ */

function CTABanner() {
  return (
    <section className="bg-gray-950 py-20 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-700/15 blur-[80px] rounded-full pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-5 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 text-blue-400 text-xs font-bold tracking-widest uppercase mb-5 bg-blue-500/10 border border-blue-500/20 px-4 py-2 rounded-full">
            <Star className="w-3.5 h-3.5 fill-blue-400" /> 57+ projects delivered across 4 countries
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">
            Start Your Digital<br />
            <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
              Transformation Today
            </span>
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto text-sm leading-relaxed">
            From discovery call to first deployment in weeks, not months.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact"
              className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-3.5 rounded-2xl transition-all inline-flex items-center justify-center gap-2 shadow-lg shadow-blue-600/30 hover:-translate-y-0.5">
              Schedule a Free Call <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services"
              className="border border-white/15 hover:border-white/30 text-white/80 hover:text-white font-semibold px-8 py-3.5 rounded-2xl transition-all inline-flex items-center justify-center gap-2">
              Browse Services
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════  CONTACT FORM  ═══════════════════════ */

function ContactForm() {
  const [state, handleSubmit] = useForm('mgvnbrve');
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  if (state.succeeded) return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <Check className="w-7 h-7 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Message Received!</h3>
      <p className="text-gray-500 text-sm">We'll respond within 24 hours.</p>
    </div>
  );

  return (
    <motion.form ref={ref} onSubmit={handleSubmit}
      variants={stagger} initial="hidden" animate={inView ? 'show' : 'hidden'}
      className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {[
          { id: 'name',  type: 'text',  label: 'Full Name',  placeholder: 'Rahul Sharma',      required: true },
          { id: 'email', type: 'email', label: 'Work Email', placeholder: 'rahul@company.com', required: true },
        ].map(f => (
          <motion.div key={f.id} variants={fade}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}</label>
            <input id={f.id} name={f.id} type={f.type} placeholder={f.placeholder} required={f.required}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm transition-all bg-gray-50 focus:bg-white" />
            <ValidationError field={f.id} prefix={f.label} errors={state.errors} className="text-xs text-red-500 mt-1" />
          </motion.div>
        ))}
      </div>
      {[
        { id: 'company', type: 'text', label: 'Company',          placeholder: 'Your company name', required: false },
        { id: 'phone',   type: 'tel',  label: 'Phone / WhatsApp', placeholder: '+91 98765 43210',   required: false },
      ].map(f => (
        <motion.div key={f.id} variants={fade}>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}</label>
          <input id={f.id} name={f.id} type={f.type} placeholder={f.placeholder}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm transition-all bg-gray-50 focus:bg-white" />
          <ValidationError field={f.id} prefix={f.label} errors={state.errors} className="text-xs text-red-500 mt-1" />
        </motion.div>
      ))}
      <motion.div variants={fade}>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message</label>
        <textarea id="message" name="message" rows={3} required placeholder="Describe your project…"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-sm transition-all bg-gray-50 focus:bg-white resize-none" />
        <ValidationError field="message" prefix="Message" errors={state.errors} className="text-xs text-red-500 mt-1" />
      </motion.div>
      <motion.button variants={fade} type="submit" disabled={state.submitting}
        className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20">
        {state.submitting ? 'Sending…' : <>Send Message <ArrowRight className="w-4 h-4" /></>}
      </motion.button>
    </motion.form>
  );
}

/* ═══════════════════════  WHY + CONTACT  ═══════════════════════ */

function WhyAndContact() {
  return (
    <Section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-5">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <SectionLabel>Why Vibhraad</SectionLabel>
            <motion.h2 variants={fade} className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
              A Partner You Can<br />Actually Count On
            </motion.h2>
            <motion.p variants={fade} className="text-gray-500 leading-relaxed mb-6 text-sm">
              Incorporated October 2025 · CIN: U62091UP2025PTC234668 · Headquartered in Noida,
              branch in Gorakhpur. Built with one purpose — enterprise-grade software that creates real business value.
            </motion.p>
            <motion.ul variants={stagger} className="space-y-3 mb-8">
              {[
                { icon: Shield,    text: 'Milestone-based payments — 50% upfront, balance on delivery' },
                { icon: Zap,       text: 'Agile delivery with weekly progress reports and live demos' },
                { icon: Trophy,    text: '57+ projects · 4 countries · 10+ industries' },
                { icon: Globe,     text: 'Active deployments: India, UK, Canada & Cambodia' },
                { icon: Users,     text: 'Dedicated support team · 24/7 SLA for enterprise clients' },
                { icon: Building2, text: 'CIN-registered · Two experienced technical directors' },
              ].map(({ icon: Icon, text }) => (
                <motion.li key={text} variants={fade} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-gray-600 text-sm leading-relaxed pt-1.5">{text}</span>
                </motion.li>
              ))}
            </motion.ul>
            <motion.div variants={fade} className="bg-white rounded-2xl p-5 border border-gray-100 mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Offices & Contact</p>
              <div className="space-y-3">
                {[
                  { label: 'Head Office · Noida',  addr: 'F-375, Sector-36, Noida, UP 201301' },
                  { label: 'Branch · Gorakhpur',   addr: '3rd Floor, Kalpana Hardware Bldg, Hata Bazar, Shivpur, UP' },
                ].map(({ label, addr }) => (
                  <div key={label} className="flex gap-3">
                    <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{label}</p>
                      <p className="text-xs text-gray-500">{addr}</p>
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t border-gray-100 space-y-2">
                  <a href="mailto:vibhraad@gmail.com" className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-blue-600 transition-colors font-medium">
                    <Mail className="w-4 h-4 text-blue-500" /> vibhraad@gmail.com
                  </a>
                  <a href="https://wa.me/7004535563" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-green-600 transition-colors font-medium">
                    <MessageCircle className="w-4 h-4 text-green-500" /> +91 7004535563 (WhatsApp)
                  </a>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                {[
                  { icon: Facebook,      href: 'https://www.facebook.com/people/Vibhraad/61579581818694/' },
                  { icon: Instagram,     href: 'https://instagram.com/vibhraad' },
                  { icon: Linkedin,      href: 'https://www.linkedin.com/company/vibhraad/' },
                  { icon: MessageCircle, href: 'https://wa.me/7004535563' },
                ].map(({ icon: Icon, href }) => (
                  <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 bg-gray-100 hover:bg-blue-100 rounded-xl flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </motion.div>
            <motion.div variants={fade}>
              <Link to="/about" className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-2xl transition-colors text-sm">
                About the Company <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>

          <motion.div variants={fade}>
            <SectionLabel>Get in Touch</SectionLabel>
            <h2 className="text-3xl font-black text-gray-900 mb-2">Ready to Build<br />Something Great?</h2>
            <p className="text-gray-400 text-sm mb-7">Share your project idea — we respond within 24 hours with a proposal and cost estimate.</p>
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100">
              <ContactForm />
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════  ROOT  ═══════════════════════ */

export default function HomePage() {
  return (
    <div className="bg-white font-sans antialiased">
      <Navigation />
      <Hero />
      <StatsAndTrust />
      <ProjectsSection />
      <SolutionsAndProcess />
      <TechAndIndustries />
      <CTABanner />
      <WhyAndContact />
      <Footer />
    </div>
  );
}
